import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { usePWA } from '@/contexts/PWAContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const BANNER_DISMISSED_KEY = 'cashee-install-banner-dismissed';
const BANNER_DELAY = 5000; // Show after 5 seconds
const BANNER_RESHOW_DELAY = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export const InstallBanner = () => {
  const { isInstallable, isInstalled } = usePWA();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Don't show if already installed or not installable
    if (isInstalled || !isInstallable) return;

    // Check if previously dismissed and if 7 days have passed
    const dismissedTimestamp = localStorage.getItem(BANNER_DISMISSED_KEY);
    if (dismissedTimestamp) {
      const timeSinceDismissal = Date.now() - parseInt(dismissedTimestamp, 10);
      if (timeSinceDismissal < BANNER_RESHOW_DELAY) {
        return; // Still within 7-day quiet period
      }
    }

    // Show after delay
    const timer = setTimeout(() => {
      setShow(true);
    }, BANNER_DELAY);

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled]);

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem(BANNER_DISMISSED_KEY, Date.now().toString());
  };

  const handleShowInstructions = () => {
    navigate('/install');
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-24 left-4 right-4 z-50 animate-fade-in">
      <div className="bg-card border border-border rounded-lg shadow-lg p-4 max-w-md mx-auto">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <h3 className="font-semibold text-sm mb-1">
              {t('pwa.banner.title')}
            </h3>
            <p className="text-xs text-muted-foreground mb-3">
              {t('pwa.banner.description')}
            </p>
            <Button
              onClick={handleShowInstructions}
              size="sm"
              className="w-full"
            >
              {t('pwa.banner.showHow')}
            </Button>
          </div>
          <button
            onClick={handleDismiss}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t('pwa.banner.close')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};
